import { 
  doc, 
  getDoc,
  updateDoc,
  serverTimestamp, 
  Firestore,
  DocumentReference
} from "firebase/firestore";
import { processLocationDataForStorage } from "./versionDataProcessingService";
import { forceFirestoreTimestamps } from "@/utils/firestoreUtils";
import { omitServerOwnedCampLocationFields } from "@/utils/serverOwnedCampLocationFields";

/**
 * Publishes a location version, making it the official published version
 */
export const publishLocationVersion = async (
  db: Firestore,
  versionId: string,
  userId: string
) => {
  try {
    console.log(`[versionPublishService] Publishing version ${versionId} with userId: ${userId}`);
    
    // Get reference to the version document
    const versionRef = doc(db, "campLocationVersions", versionId);
    
    // Create direct document reference for user (or null for system)
    const userRef = userId !== "system" ? doc(db, "users", userId) : null;
    console.log(`[versionPublishService] UserRef type:`, typeof userRef);
    
    if (userRef) {
      console.log(`[versionPublishService] UserRef path: ${userRef.path}`);
    } else {
      console.log(`[versionPublishService] UserRef is null (system user)`);
    }
    
    // Get the version data first to access the version number
    const versionSnapshot = await getDoc(versionRef);
    const versionData = versionSnapshot.data();
    if (!versionData) {
      throw new Error("Version data not found");
    }
    
    // Update the version status to published
    await updateDoc(versionRef, {
      status: "published",
      publishedAt: serverTimestamp(),
      publishedBy: userRef
    });
    
    // Get the location reference from the version
    if (!versionData.locationRef) {
      throw new Error("Version data is missing or does not have a location reference");
    }

    // IMPORTANT FIX: Get the proper location reference path from versionData.locationRef
    // and create a fresh doc reference to ensure we have all proper methods
    const locationPath = versionData.locationRef.path;
    if (!locationPath) {
      throw new Error("Location reference path is missing");
    }
    
    // Extract the document ID from the path (format: "campLocations/{docId}")
    const locationId = locationPath.split('/').pop();
    if (!locationId) {
      throw new Error("Could not extract location ID from reference path");
    }
    
    console.log(`[versionPublishService] Extracted location ID: ${locationId}`);
    
    // Publish the version data to the location document
    await publishVersionDataToLocation(db, locationId, versionData.data, versionId, userRef);
    
    console.log(`[versionPublishService] Version ${versionId} published successfully`);
    return versionId;
  } catch (error) {
    console.error("[versionPublishService] Error publishing version:", error);
    throw error;
  }
};

/**
 * Updates the location document with the published version data
 */
const publishVersionDataToLocation = async (
  db: Firestore,
  locationId: string, 
  locationData: any, 
  versionId: string,
  userRef: DocumentReference | null
) => {
  try {
    const locationRef = doc(db, "campLocations", locationId);
    
    console.log(`[versionPublishService] Updating location with version data: ${JSON.stringify(locationData).substring(0, 200)}...`);
    
    // 🔥 CRITICAL FIX: DO NOT process version data through processLocationDataForStorage!
    // Version data is already properly formatted for Firestore when it was stored.
    // Processing it again corrupts the photo timestamps by converting valid Firestore 
    // timestamps into new objects that get serialized incorrectly.
    console.log(`[versionPublishService] Using version data directly without processing to preserve photo timestamps`);
    
    // Use the version data directly - it's already in the correct Firestore format
    const updateData = {
      ...locationData,
      visibility: "v_published",
      publishedVersionId: versionId,
      latestVersionId: versionId
    };
    
    // Remove any stale lastEditedBy carried in the snapshot; the fresh stamp is attached below.
    if (updateData.lastEditedBy) {
      delete updateData.lastEditedBy;
    }
    // KB-3418: a snapshot's copy of a rollup-owned field is stale by construction; the rules refuse it.
    omitServerOwnedCampLocationFields(updateData);

    // 🔥 CRITICAL: Force all timestamp fields to proper Firestore Timestamps as final step
    const timestampSafeData = forceFirestoreTimestamps(updateData);
    console.log(`[versionPublishService] Applied forceFirestoreTimestamps before Firestore write`);

    // KB-3471: the editor stamp must ride in the SAME write as the published data (was a second
    // updateDoc). The attribution trigger resolves the actor from afterData.lastEditedBy of the write
    // that changed the model fields, so a separate stamp write credited the previous editor. userRef
    // is a raw DocumentReference (null for the system user) and lastEditedAt a serverTimestamp()
    // sentinel; both attached after forceFirestoreTimestamps, which leaves a ref/sentinel untouched
    // (proven by forceFirestoreTimestamps.kb3471.test.ts).
    console.log(`[versionPublishService] Stamping lastEditedBy=${userRef ? userRef.path : "null (system user)"} in the data write`);
    timestampSafeData.lastEditedBy = userRef;
    timestampSafeData.lastEditedAt = serverTimestamp();

    // Single write: published version data and editor stamp together.
    await updateDoc(locationRef, timestampSafeData);

    return true;
  } catch (error) {
    console.error("[versionPublishService] Error updating location with published data:", error);
    throw error;
  }
};
